// Hideo - projects store: the active project of the active profile ("All projects" = 'all', only with ≥ 2 projects)
// and the open state of the project menu at the top right (TopControls.vue).
// The choice is stored per profile in localStorage "creatorOS_project" (same key and format as before, perProfileChoice.ts)
// and read again at every call, like the legacy code did. Reactive: computeds that call active() follow the choice,
// the data (projects added or removed) and the profile switch.
import { defineStore } from 'pinia'
import { ref } from 'vue'
import { useDataStore } from './data'
import { PROJ_KEY, inActive, newItemProjectOf, projectTagHtml, resolveActive } from '../features/projects'
import { perProfileChoice } from './perProfileChoice'
import { usePedStore } from './ped'
import { useDialogsStore } from './dialogs'
import { app } from '../features/app'

export const useProjectsStore = defineStore('projects', () => {
  const data = useDataStore()
  const choice = perProfileChoice(PROJ_KEY)
  /** Bumped at every choice: computeds that read active() run again */
  const ver = choice.ver
  /** Project menu at the top right (computer) */
  const menuOpen = ref(false)

  /** Active project id, or 'all' */
  function active(): string {
    void data.rev
    return resolveActive(data.getData().projects, choice.get(data.profiles.current))
  }
  /** Items of the active project (all of them with "All projects"; always a new list) */
  function filter<T extends { projectId?: string }>(list: T[] | null | undefined): T[] {
    const a = active()
    return (list || []).filter(it => inActive(a, it))
  }
  /** Stores the choice for the active profile (no redraw: see choose) */
  function setActive(id: string): void { choice.set(data.profiles.current, id) }
  /** The user chose a project (top menu or Profiles window): format filter reset, legacy pages redrawn */
  function choose(id: string): void {
    setActive(id)
    usePedStore().filter.series = ''
    menuOpen.value = false
    app.renderAll()   // Vue pages and dialogs (the Profiles window too) redraw on refreshVue
  }
  const toggleMenu = () => { menuOpen.value = !menuOpen.value }
  const closeMenu = () => { menuOpen.value = false }

  // ===== First project (FirstProjectDialog.vue) =====
  /** Whoever waits for the first project (several calls at once too: there is only one dialog) */
  let firstWaiting: (() => void)[] = []
  /** A profile without projects must create one: shows the dialog and resolves once it is created (at once when the
   *  active profile already has projects). Bridged as ensureFirstProject (pickProfile, switchProfile, applyLoadedFile) */
  function ensureFirst(): Promise<void> {
    if (!data.currentProfile() || data.getData().projects.length) return Promise.resolve()
    return new Promise(res => {
      firstWaiting.push(res)
      const dialogs = useDialogsStore()
      if (!dialogs.isOpen('firstProject')) dialogs.show('firstProject', {})
    })
  }
  /** The first project exists: the dialog is closed and whoever waited goes on */
  function firstDone(): void {
    useDialogsStore().hide('firstProject')
    const waiting = firstWaiting
    firstWaiting = []
    waiting.forEach(f => f())
  }

  return { ver, menuOpen, active, filter, setActive, choose, toggleMenu, closeMenu, ensureFirst, firstDone }
})

/** Project of a new item: the active one, with "All projects" the first; on the Board and the Calendar with a saved plan
 *  open, the active project if it is in the plan, otherwise the plan's first (dialogs, idea box, drops, keywords…) */
export function newItemProject(): string {
  const tab = app.page
  const view = tab === 'ped' || tab === 'calendar' ? usePedStore().activeView() : null
  return newItemProjectOf(useDataStore().getData(), useProjectsStore().active(), view)
}
/** Coloured project label of an item, only with "All projects"; inPed (Board card): also with a saved plan of several
 *  projects; HTML. It reads and normalises the data and the active project at every call: lists of cards call
 *  projectTagHtml (src/features/projects.ts) with the data and the active project they already have */
export function projectTag(it: { projectId?: string } | null | undefined, cls = '', inPed = false): string {
  const view = inPed ? usePedStore().activeView() : null
  return projectTagHtml(useDataStore().getData(), it, useProjectsStore().active(), view, cls)
}
